import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const BulkListingUploader = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agent/bulk-upload/history`, auth);
      setHistory(res.data?.items || []);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agent/bulk-upload/template`, {
        ...auth, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shopperden-bulk-listings-template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Template download failed');
    }
  };

  const submit = async (file) => {
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    setUploading(true);
    setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/agent/bulk-upload`, form, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`${res.data.created_count} of ${res.data.total_rows} rows created as drafts`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const [publishing, setPublishing] = useState(false);
  const publishBatch = async (uploadId) => {
    if (!uploadId) return;
    if (!window.confirm('Publish EVERY draft in this batch? They will go live immediately across Shopperden.')) return;
    setPublishing(true);
    try {
      const res = await axios.post(`${API_URL}/api/agent/bulk-upload/${uploadId}/publish-all`, {}, auth);
      toast.success(`${res.data.published_count} listings published · now live`);
      if (result && result.upload_id === uploadId) {
        setResult({ ...result, batch_published: true, batch_published_count: res.data.published_count });
      }
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) submit(file);
  };

  return (
    <div className="space-y-6" data-testid="bulk-uploader">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#E07A3A] mb-1">Agent Tools</p>
          <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A]">Bulk Listing Uploader</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Upload up to <b>200 listings</b> at once via CSV. UK and India agencies supported.
            Every row is validated, PII-scrubbed, and saved as a draft for your review before publishing.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="bg-white border border-slate-300 hover:border-[#E07A3A] text-slate-700 px-4 py-2 text-xs font-semibold inline-flex items-center gap-2"
          data-testid="download-template-btn"
        >
          <Download className="h-3.5 w-3.5" /> Download CSV template
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`${dragging ? 'border-[#E07A3A] bg-orange-50' : 'border-slate-300 bg-white'} border-2 border-dashed p-10 text-center cursor-pointer transition-colors`}
        data-testid="bulk-drop-zone"
      >
        {uploading ? (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#E07A3A] mx-auto" />
            <p className="mt-3 text-sm text-slate-600">Uploading &amp; validating...</p>
          </>
        ) : (
          <>
            <FileSpreadsheet className="h-10 w-10 text-slate-400 mx-auto" />
            <p className="mt-3 text-sm text-slate-700 font-semibold">Drop your CSV here, or click to browse</p>
            <p className="text-xs text-slate-500 mt-1">Max 200 rows · 2 MB · UTF-8 · .csv</p>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => submit(e.target.files?.[0])}
          data-testid="bulk-file-input"
        />
      </div>

      {/* Upload result */}
      {result && (
        <div className="luxury-card p-6 space-y-4" data-testid="bulk-upload-result">
          <div className="flex flex-wrap items-center gap-4">
            <div className="bg-emerald-50 border border-emerald-200 p-3 flex-1 min-w-[180px]">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-[10px] tracking-wider uppercase text-emerald-700 font-bold">Created as drafts</span></div>
              <p className="font-['Playfair_Display'] text-3xl text-emerald-800 mt-1" data-testid="result-created">{result.created_count}</p>
            </div>
            <div className="bg-red-50 border border-red-200 p-3 flex-1 min-w-[180px]">
              <div className="flex items-center gap-2"><XCircle className="h-4 w-4 text-red-600" /><span className="text-[10px] tracking-wider uppercase text-red-700 font-bold">Rejected</span></div>
              <p className="font-['Playfair_Display'] text-3xl text-red-800 mt-1" data-testid="result-rejected">{result.rejected_count}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-3 flex-1 min-w-[180px]">
              <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">Total rows</div>
              <p className="font-['Playfair_Display'] text-3xl text-slate-800 mt-1">{result.total_rows}</p>
            </div>
          </div>

          {result.rejected_count > 0 && (
            <div className="bg-red-50 border border-red-200 p-4" data-testid="rejected-list">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-red-700" /><b className="text-sm text-red-900">Rows that need fixing</b></div>
              <ul className="space-y-1 text-xs text-red-800 max-h-48 overflow-y-auto">
                {result.rejected.slice(0, 30).map((r, i) => (
                  <li key={i} className="pl-4 border-l-2 border-red-300">
                    <b>Row {r.row}</b>: {r.errors.join(' · ')}
                  </li>
                ))}
                {result.rejected.length > 30 && (
                  <li className="text-red-700 italic">...and {result.rejected.length - 30} more</li>
                )}
              </ul>
            </div>
          )}

          <p className="text-xs text-slate-500 italic">{result.next_step}</p>

          {/* Publish-all CTA */}
          {result.created_count > 0 && !result.batch_published && (
            <div className="bg-emerald-50 border border-emerald-200 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="publish-batch-cta">
              <div className="min-w-[220px]">
                <p className="text-sm font-bold text-emerald-900">Ready to go live?</p>
                <p className="text-xs text-emerald-800 mt-1">
                  Publish all <b>{result.created_count}</b> draft listings from this batch in one click. They&apos;ll be live on Shopperden and auto-indexed by Bing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => publishBatch(result.upload_id)}
                disabled={publishing}
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-xs font-bold tracking-wider uppercase inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="publish-batch-btn"
              >
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Publish all {result.created_count} now
              </button>
            </div>
          )}
          {result.batch_published && (
            <div className="bg-emerald-100 border border-emerald-300 p-3 text-sm text-emerald-900 font-semibold" data-testid="publish-batch-done">
              ✓ {result.batch_published_count} listings published · now live on Shopperden.
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div className="luxury-card p-6" data-testid="bulk-upload-history">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-[#E07A3A]" />
          <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Recent uploads</h3>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No uploads yet — your history will appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">File</th>
                  <th className="py-2 pr-3 text-right">Created</th>
                  <th className="py-2 pr-3 text-right">Rejected</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map(h => (
                  <tr key={h.upload_id}>
                    <td className="py-2 pr-3 text-slate-600 text-xs">{new Date(h.started_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-slate-800 text-xs truncate max-w-[220px]">{h.file_name}</td>
                    <td className="py-2 pr-3 text-right text-emerald-700 font-semibold">{h.created_count}</td>
                    <td className="py-2 pr-3 text-right text-red-600 font-semibold">{h.rejected_count}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{h.total_rows}</td>
                    <td className="py-2 pr-3 text-right">
                      {h.batch_published_at ? (
                        <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5">Live ({h.batch_published_count || 0})</span>
                      ) : h.created_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => publishBatch(h.upload_id)}
                          disabled={publishing}
                          className="text-[10px] uppercase font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-700 px-2 py-0.5 disabled:opacity-60"
                          data-testid={`publish-batch-${h.upload_id}`}
                        >
                          Publish all
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help */}
      <div className="bg-[#F8FAFC] border border-slate-200 p-5 text-[12px] text-slate-700 leading-relaxed">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="h-4 w-4 text-[#E07A3A]" />
          <b>How bulk upload works</b>
        </div>
        <ul className="space-y-1 ml-4 list-disc">
          <li>Required columns: <code>title, price, city, country, property_type, listing_type</code></li>
          <li>Country codes accepted: <code>UK</code> / <code>GB</code> / <code>IN</code> / <code>IND</code> / <code>CA</code> / <code>CAN</code></li>
          <li><code>amenities</code> and <code>image_urls</code> use <b>pipe-separator</b>: <code>Parking|Garden|Lift</code></li>
          <li>Rows with phone numbers or emails in description are <b>automatically rejected</b> — Shopperden protects direct contact.</li>
          <li>Successful rows are created as <b>drafts</b> — you click Publish in your Listings tab to go live.</li>
        </ul>
      </div>
    </div>
  );
};

export default BulkListingUploader;
